/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { Fragment } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useCurrentConversationId } from '../../controllers/chat-store/useCurrentConversationId';
import { useTaskProgressActive } from '../../controllers/use-task-progress-active/use-task-progress-active';
import { AwaitingPlanAcceptanceView } from '../task-progress-view/AwaitingPlanAcceptanceView';
import { AwaitingUserResponseView } from '../task-progress-view/AwaitingUserResponseView';
import taskProgressMessages from '../task-progress-view/messages';
import { TaskCompletedView } from '../task-progress-view/TaskCompletedView';
import { TaskExecutingView } from '../task-progress-view/TaskExecutingView';

/**
 * Matches the margin styles from the chat prompt input's `wrapperMargin`
 * so overlay views align consistently with the chat input in all placements.
 */
const styles = cssMap({
	overlayMargin: {
		marginBlockStart: token('space.200'),
		marginInline: token('space.200'),
	},
	fullscreenCentered: {
		width: 'min(800px, 95vw)',
		marginInline: 'auto',
	},
});

export type ChatInputOverrideProps = {
	onSubmitOverride?: (prompt: DocNode) => void;
	placeholderOverride?: string;
};

type ChatInputOverlayProps = {
	children: (overrideProps: ChatInputOverrideProps) => React.ReactNode;
};

/**
 * Thin wrapper component that renders task overlay UIs based on the
 * screen derived by `useTaskProgressActive`.
 *
 * All state logic, pubsub handling, and screen derivation live in the hook.
 * This component only maps screen types to views.
 */
const ChatInputOverlayInner = ({
	children,
	currentConversationId,
}: ChatInputOverlayProps & { currentConversationId: string }): JSX.Element => {
	const { fullScreen } = useUIConfig();
	const { formatMessage } = useIntl();

	const { screen, handleStopTask, handleDoneClicked, onSubmitOverride } = useTaskProgressActive();
	const { sendAnalyticsEvent } = useAnalytics();

	const screenTypeToComponentName: Record<string, string> = {
		executing: 'TaskExecutingView',
		'awaiting-plan-confirmation': 'AwaitingPlanAcceptanceView',
		'awaiting-disambiguation': 'AwaitingUserResponseView',
	};

	const handleStopTaskWithAnalytics = () => {
		const invocationId =
			'invocationId' in screen ? (screen as { invocationId?: string }).invocationId : undefined;
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: screenTypeToComponentName[screen.type] ?? screen.type,
			actionSubjectId: 'cancelPlan',
			attributes: {
				source: 'taskStatusBar',
				conversationId: currentConversationId ?? '',
				invocationId,
			},
		});
		handleStopTask();
	};

	const handleDoneClickedWithAnalytics = () => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'taskProgressBarDoneButton',
			attributes: {
				source: 'taskStatusBar',
				conversationId: currentConversationId ?? '',
			},
		});
		handleDoneClicked();
	};

	if (screen.type === 'awaiting-plan-confirmation' && screen.isModifying) {
		return (
			<>
				{children({
					onSubmitOverride,
					placeholderOverride: formatMessage(taskProgressMessages.modifyPlanPlaceholder),
				})}
			</>
		);
	}

	if (screen.type === 'awaiting-disambiguation') {
		return (
			<Box xcss={cx(styles.overlayMargin, fullScreen && styles.fullscreenCentered)}>
				<AwaitingUserResponseView
					conversationId={currentConversationId}
					actionInvocationId={screen.invocationId}
					onStopTask={handleStopTaskWithAnalytics}
				/>
			</Box>
		);
	}

	if (screen.type === 'awaiting-plan-confirmation') {
		return (
			<Box xcss={cx(styles.overlayMargin, fullScreen && styles.fullscreenCentered)}>
				<AwaitingPlanAcceptanceView
					conversationId={currentConversationId}
					actionInvocationId={screen.invocationId}
					onStopTask={handleStopTaskWithAnalytics}
				/>
			</Box>
		);
	}

	if (screen.type === 'executing') {
		return (
			<Box xcss={cx(styles.overlayMargin, fullScreen && styles.fullscreenCentered)}>
				<TaskExecutingView
					todoSnapshot={screen.todoSnapshot}
					title={screen.taskTitle}
					executionSegments={screen.executionSegments}
					isPaused={screen.isPaused}
					onStopTask={handleStopTaskWithAnalytics}
				/>
			</Box>
		);
	}

	if (screen.type === 'completed') {
		return (
			<>
				<Box xcss={cx(styles.overlayMargin, fullScreen && styles.fullscreenCentered)}>
					<TaskCompletedView
						title={screen.taskTitle}
						onDoneClicked={handleDoneClickedWithAnalytics}
					/>
				</Box>
				{children({})}
			</>
		);
	}

	return <>{children({})}</>;
};

export const ChatInputOverlay = (props: ChatInputOverlayProps): JSX.Element => {
	const currentConversationId = useCurrentConversationId();
	return <ChatInputOverlayInner {...props} currentConversationId={currentConversationId} />;
};
